export interface Notification {
  id: string;
  message: string;
  timestamp: Date;
  read: boolean;
  type?: 'info' | 'success' | 'warning' | 'error';
  title?: string;
  data?: any;
}

export interface NotificationServiceConfig {
  type:
    | 'websocket'
    | 'polling'
    | 'server-sent-events'
    | 'firebase'
    | 'pusher'
    | 'ably'
    | 'custom';
  url?: string;
  apiKey?: string;
  projectId?: string;
  channel?: string;
  pollingInterval?: number;
  autoConnect?: boolean;
}

class NotificationService {
  private config: NotificationServiceConfig;
  private listeners: ((notification: Notification) => void)[] = [];
  private connection: any = null;
  public ablyClient: any = null;
  private isConnected = false;
  private pollingInterval: number | NodeJS.Timeout | null = null;

  constructor(config: NotificationServiceConfig) {
    this.config = {
      pollingInterval: 30000, // 30 seconds default
      autoConnect: true,
      ...config,
    };

    if (this.config.autoConnect) {
      this.connect();
    }
  }

  // Connect to notification service
  async connect(): Promise<void> {
    try {
      switch (this.config.type) {
        case 'websocket':
          await this.connectWebSocket();
          break;
        case 'polling':
          this.startPolling();
          break;
        case 'server-sent-events':
          await this.connectSSE();
          break;
        case 'firebase':
          await this.connectFirebase();
          break;
        case 'pusher':
          await this.connectPusher();
          break;
        case 'ably':
          await this.connectAbly();
          break;
        case 'custom':
          await this.connectCustom();
          break;
        default:
          console.warn('Unknown notification service type:', this.config.type);
      }
    } catch (error) {
      console.error('Failed to connect to notification service:', error);
    }
  }

  // Disconnect from notification service
  disconnect(): void {
    this.isConnected = false;

    if (this.connection) {
      // Clean up Ably connection properly
      if (this.connection.close) {
        try {
          this.connection.close();
        } catch (error) {
          console.error('Error closing connection:', error);
        }
      }
      // Clean up Ably channels
      if (this.connection.channels) {
        try {
          const channels = this.connection.channels.getAll();
          channels.forEach((channel: any) => {
            try {
              channel.unsubscribe();
              channel.detach();
            } catch (error) {
              console.error('Error cleaning up channel:', error);
            }
          });
        } catch (error) {
          console.error('Error cleaning up channels:', error);
        }
      }
      this.connection = null;
      this.ablyClient = null;
    }

    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }

    // Clear all listeners
    this.listeners = [];
  }

  // Subscribe to notifications
  subscribe(callback: (notification: Notification) => void): () => void {
    this.listeners.push(callback);

    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(callback);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  // Emit notification to all listeners
  private emit(notification: Notification): void {
    this.listeners.forEach((callback) => callback(notification));
  }

  // WebSocket connection
  private async connectWebSocket(): Promise<void> {
    if (!this.config.url) {
      throw new Error('WebSocket URL is required');
    }

    try {
      this.connection = new WebSocket(this.config.url);

      this.connection.onopen = () => {
        console.log('WebSocket connected');
        this.isConnected = true;
      };

      this.connection.onmessage = (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          const notification: Notification = {
            id: data.id || Date.now().toString(),
            message: data.message,
            timestamp: new Date(data.timestamp || Date.now()),
            read: false,
            type: data.type || 'info',
            title: data.title,
            data: data.data,
          };
          this.emit(notification);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      this.connection.onclose = () => {
        console.log('WebSocket disconnected');
        this.isConnected = false;
      };

      this.connection.onerror = (error: Event) => {
        console.error('WebSocket error:', error);
        this.isConnected = false;
      };
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      throw error;
    }
  }

  // Polling connection
  private startPolling(): void {
    if (!this.config.url) {
      throw new Error('Polling URL is required');
    }

    const poll = async () => {
      try {
        const response = await fetch(this.config.url!);
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data)) {
            data.forEach((item: any) => {
              const notification: Notification = {
                id: item.id || Date.now().toString(),
                message: item.message,
                timestamp: new Date(item.timestamp || Date.now()),
                read: false,
                type: item.type || 'info',
                title: item.title,
                data: item.data,
              };
              this.emit(notification);
            });
          }
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    };

    // Initial poll
    poll();

    // Set up interval
    this.pollingInterval = setInterval(poll, this.config.pollingInterval);
  }

  // Server-Sent Events connection
  private async connectSSE(): Promise<void> {
    if (!this.config.url) {
      throw new Error('SSE URL is required');
    }

    try {
      this.connection = new EventSource(this.config.url);

      this.connection.onopen = () => {
        console.log('SSE connected');
        this.isConnected = true;
      };

      this.connection.onmessage = (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          const notification: Notification = {
            id: data.id || Date.now().toString(),
            message: data.message,
            timestamp: new Date(data.timestamp || Date.now()),
            read: false,
            type: data.type || 'info',
            title: data.title,
            data: data.data,
          };
          this.emit(notification);
        } catch (error) {
          console.error('Failed to parse SSE message:', error);
        }
      };

      this.connection.onerror = (error: Event) => {
        console.error('SSE error:', error);
        this.isConnected = false;
      };
    } catch (error) {
      console.error('Failed to create SSE connection:', error);
      throw error;
    }
  }

  // Ably real-time connection
  private async connectAbly(): Promise<void> {
    try {
      // Dynamic import to avoid build issues if Ably is not available
      const Ably = await import('ably');

      if (!this.config.apiKey) {
        throw new Error('Ably API key is required');
      }

      console.log(
        'Connecting to Ably with API key:',
        this.config.apiKey.substring(0, 10) + '...'
      );

      // Create Ably client
      console.log(
        '🔧 Initializing Ably client with API key:',
        this.config.apiKey ? 'Present' : 'Missing'
      );
      const ably = new Ably.Realtime({
        key: this.config.apiKey,
        clientId: `user-${Date.now()}`, // Unique client ID
        echoMessages: false, // Don't echo our own messages
      });

      // Set up connection event handlers
      ably.connection.on('connected', () => {
        console.log('✅ Ably connected successfully');
        this.isConnected = true;
        this.connection = ably;
      });

      ably.connection.on('disconnected', () => {
        console.log('❌ Ably disconnected');
        this.isConnected = false;
      });

      ably.connection.on('failed', (error) => {
        console.error('❌ Ably connection failed:', error);
        this.isConnected = false;
        throw error;
      });

      // Subscribe to the notification channel
      const channel = ably.channels.get(this.config.channel || 'notifications');

      // Subscribe to messages
      await channel.subscribe('notification', (message) => {
        console.log('📨 Received Ably notification:', message.data);

        const notification: Notification = {
          id: message.id || Date.now().toString(),
          title: message.data.title || 'New Notification',
          message:
            message.data.message ||
            message.data.body ||
            'You have a new notification',
          timestamp: new Date(message.timestamp || Date.now()),
          read: false,
          type: message.data.type || 'info',
          data: message.data.data || message.data,
        };

        // Notify all listeners
        this.listeners.forEach((listener) => listener(notification));
      });

      console.log(
        `📡 Subscribed to Ably channel: ${
          this.config.channel || 'notifications'
        }`
      );

      // Store the connection
      this.connection = ably;
      this.ablyClient = ably; // Also store as ablyClient for compatibility
    } catch (error) {
      console.error('❌ Ably connection error:', error);

      // Fallback to websocket if Ably fails
      if (this.config.url) {
        console.log('🔄 Falling back to WebSocket...');
        await this.connectWebSocket();
      } else {
        throw new Error(
          'Ably connection failed and no websocket fallback URL provided'
        );
      }
    }
  }

  // Firebase connection (placeholder)
  private async connectFirebase(): Promise<void> {
    console.log('Firebase notification service not implemented yet');
    // TODO: Implement Firebase Cloud Messaging
  }

  // Pusher connection (placeholder)
  private async connectPusher(): Promise<void> {
    console.log('Pusher notification service not implemented yet');
    // TODO: Implement Pusher
  }

  // Custom connection (placeholder)
  private async connectCustom(): Promise<void> {
    console.log('Custom notification service not implemented yet');
    // TODO: Implement custom notification service
  }

  // Send notification (for testing)
  sendNotification(
    notification: Omit<Notification, 'id' | 'timestamp' | 'read'>
  ): void {
    const fullNotification: Notification = {
      ...notification,
      id: Date.now().toString(),
      timestamp: new Date(),
      read: false,
    };
    this.emit(fullNotification);
  }

  // Send notification via Ably real-time client
  async sendNotificationViaAbly(
    notification: Omit<Notification, 'id' | 'timestamp' | 'read'>
  ): Promise<void> {
    console.log('Sending notification via Ably:', notification);

    if (this.connection && this.isConnected) {
      try {
        const channel = this.connection.channels.get(
          this.config.channel || 'notifications'
        );

        await channel.publish('notification', {
          id: Date.now().toString(),
          title: notification.title,
          message: notification.message,
          type: notification.type,
          timestamp: new Date().toISOString(),
          data: notification.data,
        });

        console.log('✅ Notification sent via Ably');
      } catch (error) {
        console.error('❌ Failed to send notification via Ably:', error);
        throw error;
      }
    } else {
      console.warn('⚠️ Ably not connected, cannot send notification');
      throw new Error('Ably connection not available');
    }
  }

  // Get connection status
  getConnectionStatus(): boolean {
    return this.isConnected;
  }
}

// Create singleton instance
let notificationService: NotificationService | null = null;

export const createNotificationService = (
  config: NotificationServiceConfig
): NotificationService => {
  if (notificationService) {
    notificationService.disconnect();
  }

  notificationService = new NotificationService(config);
  return notificationService;
};

export const getNotificationService = (): NotificationService | null => {
  return notificationService;
};

export default NotificationService;
